/**
 * [C29] The office identity printed in every A4 header and footer (old `companySettings`: name, address, phones,
 * general manager, accountant — D-125 "identity" card). Until C29 every A4 print passed the office name only, so the
 * address / phone / manager / accountant lines that the identity card sets never printed (print captures, F-286).
 * [C29 review] Read from the session's `identity` (every signed-in user); the settings query only refreshes it for
 * holders of `settings` right after they edit the card.
 */
import { useQuery } from "@tanstack/react-query";
import type { PrintOffice } from "@faylasuf/print";
import { useApp, useSession } from "./context.js";
import { keys } from "./query.js";

const text = (v: unknown): string | undefined => (typeof v === "string" && v.trim() !== "" ? v.trim() : undefined);

export function usePrintOffice(): PrintOffice {
  const { source } = useApp();
  const { session, hasPermission } = useSession();
  const q = useQuery({ queryKey: keys.settings, queryFn: () => source.settings(), enabled: hasPermission("settings") });
  // [C35] O-4: the logo set on the identity card, fetched once per session (data URI; CSP img-src allows data:)
  const logo = useQuery({ queryKey: ["office-logo", session.print?.logo ?? false], queryFn: () => source.officeLogo(), enabled: session.print?.logo === true, staleTime: Infinity });
  const v = (k: string): string | undefined => text(q.data?.[k]?.value) ?? text(session.identity?.[k.slice("identity.".length)]);
  const out: { -readonly [K in keyof PrintOffice]: PrintOffice[K] } = { name: v("identity.company_name") ?? session.office.name };
  const fields: Array<[keyof PrintOffice, string]> = [["phone1", "identity.phone1"], ["phone2", "identity.phone2"], ["address", "identity.address"], ["managerName", "identity.manager_name"], ["accountantName", "identity.accountant_name"]];
  for (const [f, k] of fields) {
    const x = v(k);
    if (x !== undefined) (out as Record<string, string>)[f] = x;
  }
  if (logo.data?.dataUrl) out.logoUrl = logo.data.dataUrl;
  return out;
}
